import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth, getAuthHeaders } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const WIDTHS = [145,155,165,175,185,195,205,215,225,235,245,255,265,275,285,295,305,315];
const HEIGHTS = [25,30,35,40,45,50,55,60,65,70,75,80];
const DIAMETERS = [13,14,15,16,17,18,19,20,21,22,23];

export default function InterventionEditForm({ intervention, isOpen, onClose, onSuccess }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (intervention && isOpen) {
      setFormData({
        client_name: intervention.client_name || '',
        client_phone: intervention.client_phone || '',
        client_email: intervention.client_email || '',
        intervention_type: intervention.intervention_type || '',
        tire_width: intervention.tire_width ? String(intervention.tire_width) : '',
        tire_height: intervention.tire_height ? String(intervention.tire_height) : '',
        tire_diameter: intervention.tire_diameter ? String(intervention.tire_diameter) : '',
        tire_type: intervention.tire_type || '',
        tire_brand: intervention.tire_brand || '',
        tire_model: intervention.tire_model || '',
        condition_score: intervention.condition_score ? String(intervention.condition_score) : '',
        selected_stock_quantity: intervention.selected_stock_quantity || 2,
        address: intervention.address || '',
        price: intervention.price ? String(intervention.price) : '',
        delay_minutes: intervention.delay_minutes || 60,
        notes: intervention.notes || '',
        vehicle_plate: intervention.vehicle_plate || '',
        vehicle_brand: intervention.vehicle_brand || '',
        vehicle_model: intervention.vehicle_model || '',
      });
    }
  }, [intervention, isOpen]);

  const canEdit = () => {
    if (!intervention || !user) return false;
    const status = intervention.status;
    const role = user.role;
    const operateurStatuses = ['en_attente', 'validee', 'en_route', 'en_cours'];
    if (role === 'admin') return true;
    if (role === 'operateur') return operateurStatuses.includes(status);
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit()) return;
    setLoading(true);
    try {
      const payload = {};
      const orig = intervention;
      if (formData.client_name !== orig.client_name) payload.client_name = formData.client_name;
      if (formData.client_phone !== orig.client_phone) payload.client_phone = formData.client_phone;
      if (formData.client_email !== (orig.client_email || '')) payload.client_email = formData.client_email;
      if (formData.intervention_type !== orig.intervention_type) payload.intervention_type = formData.intervention_type;
      if (formData.tire_width && parseInt(formData.tire_width) !== orig.tire_width) payload.tire_width = parseInt(formData.tire_width);
      if (formData.tire_height && parseInt(formData.tire_height) !== orig.tire_height) payload.tire_height = parseInt(formData.tire_height);
      if (formData.tire_diameter && parseInt(formData.tire_diameter) !== orig.tire_diameter) payload.tire_diameter = parseInt(formData.tire_diameter);
      if (formData.tire_type !== (orig.tire_type || '')) payload.tire_type = formData.tire_type;
      if (formData.tire_brand !== (orig.tire_brand || '')) payload.tire_brand = formData.tire_brand;
      if (formData.tire_model !== (orig.tire_model || '')) payload.tire_model = formData.tire_model;
      if (formData.condition_score && parseInt(formData.condition_score) !== orig.condition_score) payload.condition_score = parseInt(formData.condition_score);
      if (parseInt(formData.selected_stock_quantity) !== (orig.selected_stock_quantity || 2)) payload.selected_stock_quantity = parseInt(formData.selected_stock_quantity);
      if (formData.address !== orig.address) payload.address = formData.address;
      if (parseFloat(formData.price) !== orig.price) payload.price = parseFloat(formData.price);
      if (parseInt(formData.delay_minutes) !== (orig.delay_minutes || 60)) payload.delay_minutes = parseInt(formData.delay_minutes);
      if (formData.notes !== (orig.notes || '')) payload.notes = formData.notes;
      if (formData.vehicle_plate !== (orig.vehicle_plate || '')) payload.vehicle_plate = formData.vehicle_plate;
      if (formData.vehicle_brand !== (orig.vehicle_brand || '')) payload.vehicle_brand = formData.vehicle_brand;
      if (formData.vehicle_model !== (orig.vehicle_model || '')) payload.vehicle_model = formData.vehicle_model;

      if (Object.keys(payload).length === 0) {
        toast.info('Aucune modification détectée');
        onClose();
        return;
      }

      await axios.put(`${API}/api/interventions/${intervention.id}`, payload, {
        headers: getAuthHeaders()
      });
      toast.success('Intervention mise à jour');
      onSuccess?.();
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Erreur lors de la mise à jour';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!intervention) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-heading text-gray-900">Modifier l'intervention</DialogTitle>
          <DialogDescription className="text-gray-500">
            {intervention.client_name} — {intervention.intervention_type}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Client */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Nom du client *</Label>
              <Input value={formData.client_name || ''} onChange={(e) => setFormData(p => ({ ...p, client_name: e.target.value }))} required className="bg-white border-gray-300" data-testid="edit-client-name" />
            </div>
            <div className="space-y-2">
              <Label>Téléphone *</Label>
              <Input value={formData.client_phone || ''} onChange={(e) => setFormData(p => ({ ...p, client_phone: e.target.value }))} required className="bg-white border-gray-300" data-testid="edit-client-phone" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={formData.client_email || ''} onChange={(e) => setFormData(p => ({ ...p, client_email: e.target.value }))} className="bg-white border-gray-300" />
            </div>
            <div className="space-y-2">
              <Label>Type d'intervention *</Label>
              <Select value={formData.intervention_type} onValueChange={(v) => setFormData(p => ({ ...p, intervention_type: v }))}>
                <SelectTrigger className="bg-white border-gray-300" data-testid="edit-intervention-type">
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Remplacement pneus">Remplacement pneus</SelectItem>
                  <SelectItem value="Réparation crevaison">Réparation crevaison</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Tires */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Pneus</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Quantité</Label>
                <Input type="number" min="1" max="10" value={formData.selected_stock_quantity || ''} onChange={(e) => setFormData(p => ({ ...p, selected_stock_quantity: parseInt(e.target.value) || 1 }))} className="bg-white border-gray-300" data-testid="edit-tire-quantity" />
              </div>
              <div className="space-y-2">
                <Label>Largeur</Label>
                <Select value={formData.tire_width} onValueChange={(v) => setFormData(p => ({ ...p, tire_width: v }))}>
                  <SelectTrigger className="bg-white border-gray-300"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{WIDTHS.map(w => <SelectItem key={w} value={String(w)}>{w}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Hauteur</Label>
                <Select value={formData.tire_height} onValueChange={(v) => setFormData(p => ({ ...p, tire_height: v }))}>
                  <SelectTrigger className="bg-white border-gray-300"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{HEIGHTS.map(h => <SelectItem key={h} value={String(h)}>{h}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Diamètre</Label>
                <Select value={formData.tire_diameter} onValueChange={(v) => setFormData(p => ({ ...p, tire_diameter: v }))}>
                  <SelectTrigger className="bg-white border-gray-300"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{DIAMETERS.map(d => <SelectItem key={d} value={String(d)}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Type pneus</Label>
                <Select value={formData.tire_type} onValueChange={(v) => setFormData(p => ({ ...p, tire_type: v }))}>
                  <SelectTrigger className="bg-white border-gray-300"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Neuf">Neuf</SelectItem>
                    <SelectItem value="Occasion">Occasion</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Marque</Label>
                <Input value={formData.tire_brand || ''} onChange={(e) => setFormData(p => ({ ...p, tire_brand: e.target.value }))} className="bg-white border-gray-300" />
              </div>
              <div className="space-y-2">
                <Label>Modèle</Label>
                <Input value={formData.tire_model || ''} onChange={(e) => setFormData(p => ({ ...p, tire_model: e.target.value }))} className="bg-white border-gray-300" />
              </div>
            </div>
          </div>

          {/* Address + Price */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Adresse *</Label>
              <Input value={formData.address || ''} onChange={(e) => setFormData(p => ({ ...p, address: e.target.value }))} required className="bg-white border-gray-300" data-testid="edit-address" />
            </div>
            <div className="space-y-2">
              <Label>Prix (EUR) *</Label>
              <Input type="number" step="0.01" value={formData.price || ''} onChange={(e) => setFormData(p => ({ ...p, price: e.target.value }))} required className="bg-white border-gray-300" data-testid="edit-price" />
            </div>
          </div>

          {/* Delay */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Délai (minutes)</Label>
              <Input type="number" value={formData.delay_minutes || ''} onChange={(e) => setFormData(p => ({ ...p, delay_minutes: parseInt(e.target.value) || 60 }))} className="bg-white border-gray-300" data-testid="edit-delay" />
              <p className="text-xs text-gray-400">Modifier le délai recalcule l'heure annoncée</p>
            </div>
          </div>

          {/* Vehicle */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Véhicule</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Immatriculation</Label>
                <Input value={formData.vehicle_plate || ''} onChange={(e) => setFormData(p => ({ ...p, vehicle_plate: e.target.value.toUpperCase() }))} placeholder="AB-123-CD" className="bg-white border-gray-300 uppercase" data-testid="edit-vehicle-plate" />
              </div>
              <div className="space-y-2">
                <Label>Marque</Label>
                <Input value={formData.vehicle_brand || ''} onChange={(e) => setFormData(p => ({ ...p, vehicle_brand: e.target.value }))} className="bg-white border-gray-300" data-testid="edit-vehicle-brand" />
              </div>
              <div className="space-y-2">
                <Label>Modèle</Label>
                <Input value={formData.vehicle_model || ''} onChange={(e) => setFormData(p => ({ ...p, vehicle_model: e.target.value }))} className="bg-white border-gray-300" data-testid="edit-vehicle-model" />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea value={formData.notes || ''} onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))} className="bg-white border-gray-300 min-h-[80px]" data-testid="edit-notes" />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="border-gray-300">Annuler</Button>
            <Button type="submit" disabled={loading} className="bg-reco-500 hover:bg-reco-600 text-white rounded-full px-6" data-testid="edit-submit">
              {loading ? 'Enregistrement...' : 'Enregistrer les modifications'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
